/* eslint-disable @typescript-eslint/unbound-method */
import { err, ok, Result } from 'neverthrow';
import {
  Constant,
  Neq,
  Eq,
  f,
  IsNull,
  Not,
  NullIf,
  Case,
  Or,
  And,
  Desc,
  Asc,
  CosineDistance,
  EmptyArrayOf,
  MakeArray,
} from './utilities';
import _ from 'lodash';
import type { AST } from '../ast';
import { Ty } from '../ty';
import { TC, TyStackTrace } from '../type-checker';
import { CountOver } from './window';
import { expandExpr } from '../macros/expand-expr';
import { Macro } from '../macros';
import { evalExprIR } from '../interpreter/eval-expr-ir';
import type { ExprTypeCheck } from '../type-checker/expr/check-expr';
import { GLOBAL_SCOPE } from './macro/macro-base';
import { Markup } from './markup';
import { Relation } from './relation';
import { RelationRef } from './relation-ref';
import {
  CantDoAMatchOnNonEnums,
  MissingMatchVariants,
} from '../type-checker/type-check-error';
import { MarkupShorthand } from './markup/section';
import { M } from '.';
import type { EraVar } from './era-var';
import { Errs } from '../type-checker/type-checker';
import { EraQL } from '../eraql';
import { _PickListItem } from '../ast/mu';

const EXPRESSION_CACHE: WeakMap<
  AST.ExprFR,
  WeakRef<Expression>
> = new WeakMap();

export class Expression {
  protected constructor(
    readonly ast: AST.ExprFR,
    private readonly typecheck: ExprTypeCheck
  ) {}

  toEraQL(): string {
    return EraQL.exprToEraQL(this.ast, { refs: {} });
  }

  eraql(strings: TemplateStringsArray, ...values: any[]): Expression {
    const current = this.toEraQL();
    // Create a new TemplateStringsArray with the current expression prepended
    const newStrings = Object.assign(
      [current + (strings[0] || '')].concat(Array.from(strings).slice(1)),
      {
        raw: [current + (strings.raw[0] || '')].concat(
          Array.from(strings.raw).slice(1)
        ),
      }
    ) as unknown as TemplateStringsArray;

    const compiler = EraQL.makeEraqlExpressionCompiler(
      { attributes: this.attrReqs['from'] },
      { jsStackPointer: this.eraql }
    );

    return compiler(newStrings, ...values);
  }

  toString() {
    const e = Error(
      `Trying to convert an Expression to a string, did you mean to use the "${f.name}" helper on a template string?`
    );

    if ((Error as any).captureStackTrace) {
      (Error as any).captureStackTrace(e, this.toString);
    }

    throw e;
  }

  static wrap(
    expr: Ty.Scalar | null | Expression,
    opts?: { ty?: Ty.Shorthand }
  ): Expression {
    return expr instanceof Expression ? expr : Constant(expr, opts);
  }

  static fromAst(
    ast: AST.ExprFR,
    opts?: { jsStackPointer?: Function }
  ): Expression {
    const checked = this.tryfromAst(ast);

    if (checked.isOk()) {
      return checked.value;
    }

    throw checked.error.toError({
      jsStackPointer: opts?.jsStackPointer ?? this.fromAst,
    });
  }

  static tryfromAst(ast: AST.ExprFR): Result<Expression, TyStackTrace> {
    const existing = EXPRESSION_CACHE.get(ast)?.deref();

    if (existing) {
      return ok(existing);
    }

    const checked = TC.checkExpr(ast);

    if (checked instanceof TyStackTrace) {
      return err(checked);
    }

    const expr = new Expression(ast, checked);
    Object.freeze(expr);
    EXPRESSION_CACHE.set(ast, new WeakRef(expr));
    return ok(expr);
  }

  tag(...tags: string[]): Expression {
    return tags.reduce<Expression>(
      (expr, tag) => expr.functionCall('tag', [tag]),
      this
    );
  }

  andThen(cb: (expr: Expression) => Expression): Expression {
    return cb(this);
  }

  get freeVars(): { readonly [scope: string]: AST.MacroArgsType } {
    return this.typecheck.vars;
  }

  get ty(): Ty.ExtendedAttributeType {
    return this.typecheck.ty;
  }

  get attrReqs() {
    return this.typecheck.attrReqs;
  }

  displayTy(): string {
    return Ty.displayTy(this.ty);
  }

  get isAggregated(): boolean {
    return this.typecheck.aggregated;
  }

  get isWindowed(): boolean {
    return this.typecheck.windowed;
  }

  implements(req: Ty.Shorthand): boolean {
    return TC.implementsTy({ subject: this.ty, req });
  }

  get(name: null | string | Expression): Expression {
    const otherArg = name === null ? Constant(null, { ty: 'string' }) : name;
    return this.functionCall('get_from_record', [otherArg], {
      jsStackPointer: this.get,
    });
  }

  getField(first: string, ...path: readonly string[]): Expression {
    return path.reduce(
      (expr, part) => expr.getField(part),
      Expression.fromAst(
        { t: 'get-field', name: first, expr: this.ast },
        {
          jsStackPointer: this.getField,
        }
      )
    );
  }

  isNull(): Expression {
    return IsNull(this);
  }

  isNotNull(opts?: { jsStackPointer?: Function }): Expression {
    return this.functionCall('is_not_null', [], {
      jsStackPointer: opts?.jsStackPointer ?? this.isNotNull,
    });
  }

  coalesce(
    expr: Ty.Scalar | Expression,
    opts?: { jsStackPointer?: Function }
  ): Expression {
    return this.functionCall('coalesce', [expr], {
      jsStackPointer: opts?.jsStackPointer ?? this.coalesce,
    });
  }

  oneOf(
    options: readonly (
      | boolean
      | string
      | number
      | Date
      | Record<string, Ty.Scalar>
      | Expression
    )[],
    opts?: { jsStackPointer?: Function }
  ): Expression {
    const args = [
      options.length === 0 ? EmptyArrayOf(this.ty) : MakeArray(options),
    ];

    return this.functionCall('one_of', args, {
      jsStackPointer: opts?.jsStackPointer ?? this.oneOf,
    });
  }

  between(min: Ty.Scalar | Expression, max: Ty.Scalar | Expression) {
    const opts = {
      jsStackPointer: this.between,
    };
    return this.gte(min, opts).and(this.lte(max, opts));
  }

  cast(targetTy: Ty.Shorthand): Expression {
    return Expression.fromAst(
      {
        t: 'cast',
        targetTy: Ty.shorthandToTy(targetTy).ty,
        expr: this.ast,
      },
      {
        jsStackPointer: this.cast,
      }
    );
  }

  concat(other: string | Expression, opts?: { jsStackPointer?: Function }) {
    return this.functionCall('concat', [other], {
      jsStackPointer: opts?.jsStackPointer ?? this.concat,
    });
  }

  like(
    other: string | Expression,
    opts?: { jsStackPointer: Function }
  ): Expression {
    return this.functionCall('like', [other], {
      jsStackPointer: opts?.jsStackPointer ?? this.like,
    });
  }

  eq(
    other: Ty.Scalar | Expression,
    opts?: { jsStackPointer?: Function; display?: AST.FunctionCallDisplay }
  ): Expression {
    return Eq(this, other, {
      jsStackPointer: opts?.jsStackPointer ?? this.eq,
      display: opts?.display ?? 'infix',
    });
  }

  neq(other: Ty.Scalar | Expression): Expression {
    return Neq(this, other, { jsStackPointer: this.neq });
  }

  isDistinctFrom(other: Ty.Scalar | Expression): Expression {
    const r = other instanceof Expression ? other : Constant(other);
    return Or(
      And(this.isNull(), r.isNotNull()),
      And(this.isNotNull(), r.isNull()),
      And(And(this.isNotNull(), r.isNotNull()), Neq(this, r))
    );
  }

  lower(opts?: { jsStackPointer: Function }): Expression {
    return this.functionCall('lower', [], {
      jsStackPointer: opts?.jsStackPointer ?? this.lower,
    });
  }

  upper(opts?: { jsStackPointer: Function }): Expression {
    return this.functionCall('upper', [], {
      jsStackPointer: opts?.jsStackPointer ?? this.upper,
    });
  }

  splitPart(
    delim: string | Expression,
    part: number | Expression,
    opts?: { jsStackPointer?: Function }
  ) {
    return this.functionCall('split_part', [delim, part], {
      jsStackPointer: opts?.jsStackPointer ?? this.splitPart,
    });
  }

  replace(
    oldChars: string | Expression,
    newChars: string | Expression,
    opts?: { jsStackPointer?: Function }
  ): Expression {
    return this.functionCall('replace', [oldChars, newChars], {
      jsStackPointer: opts?.jsStackPointer ?? this.replace,
    });
  }

  md5(opts?: { jsStackPointer: Function }): Expression {
    return this.functionCall('md5', [], {
      jsStackPointer: opts?.jsStackPointer ?? this.md5,
    });
  }

  length(opts?: { jsStackPointer: Function }): Expression {
    return this.functionCall('length', [], {
      jsStackPointer: opts?.jsStackPointer ?? this.length,
    });
  }

  functionCall(
    op: AST.FunctionIdentifier,
    args: readonly (Ty.Scalar | Expression)[],
    opts?: { jsStackPointer?: Function; display?: AST.FunctionCallDisplay }
  ): Expression {
    const argExprs = [
      // Use `this` as the first argument
      this,
      ...args.map((arg) => (arg instanceof Expression ? arg : Constant(arg))),
    ];

    const expr: AST._FunctionCall<AST.ExprMacroChildren> = {
      t: 'function-call',
      op,
      args: argExprs.map((arg) => arg.ast),
    };

    return Expression.fromAst(expr, {
      jsStackPointer: opts?.jsStackPointer ?? this.functionCall,
    });
  }

  and(
    expr: boolean | Expression,
    opts?: { jsStackPointer?: Function; display?: AST.FunctionCallDisplay }
  ): Expression {
    return this.functionCall('and', [expr], {
      jsStackPointer: opts?.jsStackPointer ?? this.and,
      display: opts?.display ?? 'infix',
    });
  }

  or(
    expr: boolean | Expression,
    opts?: { jsStackPointer?: Function; display?: AST.FunctionCallDisplay }
  ): Expression {
    return this.functionCall('or', [expr], {
      jsStackPointer: opts?.jsStackPointer ?? this.or,
      display: opts?.display ?? 'infix',
    });
  }

  gt(
    expr: Ty.Scalar | Expression,
    opts?: { jsStackPointer?: Function; display?: AST.FunctionCallDisplay }
  ): Expression {
    return this.functionCall('gt', [expr], {
      jsStackPointer: opts?.jsStackPointer ?? this.gt,
      display: opts?.display ?? 'infix',
    });
  }

  gte(
    expr: Ty.Scalar | Expression,
    opts?: { jsStackPointer?: Function; display?: AST.FunctionCallDisplay }
  ): Expression {
    return this.functionCall('gte', [expr], {
      jsStackPointer: opts?.jsStackPointer ?? this.gte,
      display: opts?.display ?? 'infix',
    });
  }

  lt(
    expr: Ty.Scalar | Expression,
    opts?: { jsStackPointer?: Function; display?: AST.FunctionCallDisplay }
  ): Expression {
    return this.functionCall('lt', [expr], {
      jsStackPointer: opts?.jsStackPointer ?? this.lt,
      display: opts?.display ?? 'infix',
    });
  }

  lte(
    expr: Ty.Scalar | Expression,
    opts?: { jsStackPointer?: Function; display?: AST.FunctionCallDisplay }
  ): Expression {
    return this.functionCall('lte', [expr], {
      jsStackPointer: opts?.jsStackPointer ?? this.lte,
      display: opts?.display ?? 'infix',
    });
  }

  add(
    expr: number | Expression,
    opts?: { jsStackPointer?: Function; display?: AST.FunctionCallDisplay }
  ): Expression {
    return this.functionCall('add', [expr], {
      jsStackPointer: opts?.jsStackPointer ?? this.add,
      display: opts?.display ?? 'infix',
    });
  }

  sub(
    expr: number | Expression,
    opts?: { jsStackPointer?: Function; display?: AST.FunctionCallDisplay }
  ): Expression {
    return this.functionCall('sub', [expr], {
      jsStackPointer: opts?.jsStackPointer ?? this.sub,
      display: opts?.display ?? 'infix',
    });
  }

  div(
    expr: number | Expression,
    opts?: { jsStackPointer?: Function; display?: AST.FunctionCallDisplay }
  ): Expression {
    return this.functionCall('div', [expr], {
      jsStackPointer: opts?.jsStackPointer ?? this.div,
      display: opts?.display ?? 'infix',
    });
  }

  floor(opts?: { jsStackPointer?: Function }): Expression {
    return this.functionCall('floor', [], {
      jsStackPointer: opts?.jsStackPointer ?? this.floor,
    });
  }

  ceil(opts?: { jsStackPointer?: Function }): Expression {
    return this.functionCall('ceil', [], {
      jsStackPointer: opts?.jsStackPointer ?? this.floor,
    });
  }

  toThePowerOf(expr: number | Expression): Expression {
    return this.functionCall('to_the_power_of', [expr], {
      jsStackPointer: this.toThePowerOf,
    });
  }

  safeDiv(expr: Ty.Scalar | Expression): Expression {
    return this.functionCall('div', [NullIf(expr, 0)], {
      jsStackPointer: this.div,
    });
  }

  mul(
    expr: Ty.Scalar | Expression,
    opts?: { jsStackPointer?: Function }
  ): Expression {
    return this.functionCall('mul', [expr], {
      jsStackPointer: opts?.jsStackPointer ?? this.mul,
    });
  }

  not(opts?: { jsStackPointer?: Function }): Expression {
    return this.functionCall('not', [], {
      jsStackPointer: opts?.jsStackPointer ?? this.not,
    });
  }

  match(
    cases: Record<string, Ty.Scalar | Expression>,
    opts?: { jsStackPointer?: Function }
  ): Expression {
    const { ty } = this.ty;
    if (ty.k !== 'enum') {
      throw new CantDoAMatchOnNonEnums({ type: this.ty }).toStackTrace({
        frame: this.ast,
      });
    }

    const missingVariants = ty.t.filter(
      (variant) => cases[variant] === undefined
    );

    if (missingVariants.length > 0) {
      throw new Errs.MissingMatchVariants({ missingVariants }).toStackTrace({
        frame: this.ast,
      });
    }

    const jsStackPointer: Function = opts?.jsStackPointer ?? this.match;

    return Case(
      Object.entries(cases).map(([key, val]) => ({
        when: this.eq(key, { jsStackPointer }),
        then: val,
      })),
      { jsStackPointer }
    );
  }

  abs(opts?: { jsStackPointer?: Function }): Expression {
    return this.functionCall('abs', [], {
      jsStackPointer: opts?.jsStackPointer ?? this.abs,
    });
  }

  round(
    amount: number | Expression,
    opts?: { jsStackPointer?: Function }
  ): Expression {
    return this.functionCall('round', [amount], {
      jsStackPointer: opts?.jsStackPointer ?? this.round,
    });
  }

  clamp(
    lowerBound: Ty.Scalar | Expression,
    upperBound: Ty.Scalar | Expression
  ): Expression {
    const opts = { jsStackPointer: this.clamp };

    return Case(
      [
        { when: this.lt(lowerBound, opts), then: lowerBound },
        { when: this.gt(upperBound, opts), then: upperBound },
      ],
      { else: this, ...opts }
    );
  }

  dateDiff(
    end: Date | Expression,
    timeframe: 'days' | 'years' | 'seconds',

    opts?: { jsStackPointer?: Function }
  ) {
    return this.functionCall('date_diff', [end, timeframe], {
      jsStackPointer: opts?.jsStackPointer ?? this.dateDiff,
    });
  }

  dateAdd(
    timeframe: 'days' | 'weeks',
    amount: number | Expression,
    opts?: { jsStackPointer?: Function }
  ): Expression {
    const newOpts = { jsStackPointer: opts?.jsStackPointer ?? this.dateAdd };
    const rhs = amount instanceof Expression ? amount : Constant(amount);
    switch (timeframe) {
      case 'days':
        return this.functionCall('date_add', [rhs, 'days'], newOpts);
      case 'weeks':
        return this.functionCall('date_add', [rhs.mul(7), 'days'], newOpts);
    }
  }

  dateSub(
    timeframe: 'days' | 'weeks',
    amount: number | Expression,
    opts?: { jsStackPointer?: Function }
  ): Expression {
    const { jsStackPointer = this.dateSub } = opts ?? {};
    const amt = amount instanceof Expression ? amount : Constant(amount);
    return this.dateAdd(timeframe, amt.mul(-1, { jsStackPointer }), {
      jsStackPointer,
    });
  }

  datePart(
    timeframe:
      | 'seconds'
      | 'minute'
      | 'hour'
      | 'day'
      | 'dow'
      | 'week'
      | 'month'
      | 'quarter'
      | 'year'
  ): Expression {
    return this.functionCall('date_part', [timeframe]);
  }

  removePrefix(
    prefix: string | Expression,
    opts?: { jsStackPointer?: Function }
  ): Expression {
    return this.functionCall('remove_prefix', [prefix], {
      jsStackPointer: opts?.jsStackPointer ?? this.removePrefix,
    });
  }

  removeSuffix(
    suffix: string | Expression,
    opts?: { jsStackPointer?: Function }
  ): Expression {
    return this.functionCall('remove_suffix', [suffix], {
      jsStackPointer: opts?.jsStackPointer ?? this.removeSuffix,
    });
  }

  categoryConcat(
    rhs: string | Expression | null,
    opts?: { jsStackPointer?: Function }
  ): Expression {
    return this.functionCall('category_concat', [rhs], {
      jsStackPointer: opts?.jsStackPointer ?? this.categoryConcat,
    });
  }

  categoryAt(
    as: number | Expression | null,
    opts?: { jsStackPointer?: Function }
  ): Expression {
    return this.functionCall('category_at', [as], opts);
  }

  categoryAfter(prefix: string | Expression): Expression {
    return this.removePrefix(prefix.concat(Ty.ENUM_PATH_SEPERATOR)).categoryAt(
      0
    );
  }

  substring(
    start: number | Expression,
    length: number | Expression,
    opts?: { jsStackPointer?: Function }
  ): Expression {
    return this.functionCall('substring', [start, length], {
      jsStackPointer: opts?.jsStackPointer,
    });
  }

  startsWith(
    matcher: string | Expression,
    opts?: { jsStackPointer?: Function }
  ): Expression {
    return this.functionCall('starts_with', [matcher], opts);
  }

  endsWith(
    matcher: string | Expression,
    opts?: { jsStackPointer?: Function }
  ): Expression {
    return this.functionCall('ends_with', [matcher], opts);
  }

  dateFmt(fmt: string): Expression {
    return this.functionCall('date_fmt', [fmt]);
  }

  dateTrunc(
    timeframe: 'minute' | 'hour' | 'day' | 'week' | 'month' | 'quarter' | 'year'
  ): Expression {
    return this.functionCall('date_trunc', [timeframe]);
  }

  desc() {
    return Desc(this);
  }

  asc() {
    return Asc(this);
  }

  assertDistinct(params?: { key?: string }): Expression {
    return this.invariants({
      [`${params?.key ? `${params.key} ` : ''}Is Unique`]: CountOver(true, {
        partitionBy: [this],
      }).eq(1),
    });
  }

  assertNotNull(params?: { key?: string }): Expression {
    return this.invariants({
      [`${params?.key ? `${params.key} ` : ''}Is Not Null`]: Not(IsNull(this)),
    });
  }

  tap(callback: (expr: Expression) => void): Expression {
    callback(this);
    return this;
  }

  ir(): AST.ExprIR {
    return expandExpr(this.ast, {});
  }

  isNumericString() {
    return this.functionCall('is_numeric_string', []);
  }

  invariants(invariants: {
    readonly [name: string]: boolean | Expression;
  }): Expression {
    const invs = _.mapValues(invariants, (expr) => Expression.wrap(expr));

    return Expression.fromAst({
      t: 'invariants',
      expr: this.ast,
      invariants: _.mapValues(invs, ({ ast: expr }) => expr),
    });
  }

  impure(): Expression {
    return this.functionCall('impure', []);
  }

  tryEvaluate(opts?: {
    allow?: { now?: boolean; rand?: boolean; undefaultedVars?: boolean };
  }): Result<Ty.Scalar, { t: 'cant-evaluate' }> {
    if (!TC.isExprInterpretable(this.ast, opts)) {
      return err({ t: 'cant-evaluate' });
    }

    return ok(this.evaluate());
  }

  evaluate(mocks?: {
    now?: Date;
    sources?: Partial<Record<'from' | 'left' | 'right', Ty.Row>>;
    rows?: Ty.Row[];
  }): Ty.Scalar {
    const { sources = {}, rows = [] } = mocks ?? {};
    const inner = mocks?.now
      ? Macro.callExprMacro(
          this.ast,
          { exprs: { now: Constant(mocks.now).ast } },
          { scope: GLOBAL_SCOPE }
        ).ir()
      : this.ir();

    return evalExprIR(inner, { rows, sources });
  }

  cosineDistance(other: number[] | Expression): Expression {
    return CosineDistance(this, other);
  }
}

export class ExprVar extends Expression implements EraVar {
  protected constructor(
    readonly exprVar: AST._ExprVar<AST.ExprMacroChildren>,
    typecheck: ExprTypeCheck
  ) {
    super(exprVar, typecheck);
    Object.freeze(this);
  }

  variableId(): { readonly name: string; readonly scope: string } {
    return { name: this.exprVar.name, scope: this.exprVar.scope };
  }

  muMatch(cases: Record<string, MarkupShorthand>): Markup {
    const problem = this.#checkMatch(cases, { jsStackPointer: this.muMatch });

    if (problem !== null) {
      throw problem;
    }

    return M.muCase(
      Object.entries(cases).map(([val, then]) => ({ when: this.eq(val), then }))
    );
  }

  withNewDefault(newDefault?: Ty.Scalar | Expression) {
    return ExprVar.create({
      name: this.exprVar.name,
      scope: this.exprVar.scope,
      ty: this.exprVar.ty,
      default: newDefault,
    });
  }

  Controls(opts?: { label?: boolean }) {
    return Markup.fromAst({
      t: 'expr-controls-v2',
      var: this.exprVar,
      config: null,
      label: opts?.label ?? true,
    });
  }

  CategoryTabs(
    rel: Relation,
    option: (t: RelationRef) => Expression,
    opts?: {
      jsStackPointer?: Function;
      label?: boolean;
    }
  ): Markup {
    return Markup.fromAst(
      {
        t: 'expr-controls-v2',
        var: this.exprVar,
        label: opts?.label ?? true,
        config: {
          t: 'pickfrom',
          rel: rel.select((t) => ({ option: option(t) }), { distinct: true })
            .ast,
          display: 'category-tabs',
        },
      },
      { jsStackPointer: opts?.jsStackPointer ?? this.PickList }
    );
  }

  Nav(
    options?: readonly string[],
    opts?: {
      jsStackPointer?: Function;
      items?: readonly _PickListItem[];
    }
  ) {
    return this.PickList(options, {
      jsStackPointer: opts?.jsStackPointer ?? this.Nav,
      display: 'nav',
      label: false,
    });
  }

  Toggle(
    options?: readonly string[],
    opts?: { jsStackPointer?: Function }
  ): Markup {
    return this.PickList(options, {
      ...opts,
      jsStackPointer: opts?.jsStackPointer ?? this.Toggle,
      display: 'toggle',
    });
  }

  PickFrom(
    rel: Relation,
    option: (t: RelationRef) => Expression,
    opts?: {
      jsStackPointer?: Function;
      display?: 'picklist' | 'toggle' | 'tab-selector' | 'category-tabs';
      label?: boolean;
    }
  ): Markup {
    return Markup.fromAst(
      {
        t: 'expr-controls-v2',
        var: this.exprVar,
        label: opts?.label ?? true,
        config: {
          t: 'pickfrom',
          rel: rel.select((t) => ({ option: option(t) }), { distinct: true })
            .ast,
          display: opts?.display ?? 'picklist',
        },
      },
      { jsStackPointer: opts?.jsStackPointer ?? this.PickList }
    );
  }

  TabSelector(
    options?: readonly string[],
    opts?: { jsStackPointer?: Function }
  ) {
    return this.PickList(options, {
      jsStackPointer: opts?.jsStackPointer ?? this.TabSelector,
      display: 'tab-selector',
      label: false,
    });
  }

  PickList(
    options?: readonly string[],
    opts?: {
      jsStackPointer?: Function;
      display?:
        | 'picklist'
        | 'toggle'
        | 'tab-selector'
        | 'nav'
        | 'category-tabs';
      label?: boolean;
    }
  ): Markup {
    let inferedOpts: readonly string[] | null = null;

    if (options) {
      inferedOpts = options;
    } else {
      if (this.ty.ty.k === 'enum') {
        inferedOpts = this.ty.ty.t;
      } else {
        throw new Error(
          `Cant infer options for type "${Ty.displayTy(
            this.ty
          )}", please provide options`
        );
      }
    }

    return Markup.fromAst(
      {
        t: 'expr-controls-v2',
        var: this.exprVar,
        label: opts?.label ?? true,
        config: {
          t: 'picklist',
          options: inferedOpts,
          display: opts?.display ?? 'picklist',
        },
      },
      { jsStackPointer: opts?.jsStackPointer ?? this.PickList }
    );
  }

  static create(params: {
    name: string;
    scope: string;
    ty: Ty.Shorthand;
    default?: Expression | Ty.Scalar;
  }): ExprVar {
    const ast: AST._ExprVar<AST.ExprMacroChildren> = {
      t: 'expr-var',
      name: params.name,
      scope: params.scope,
      ty: Ty.shorthandToTy(params.ty),
      default:
        params.default !== undefined
          ? Expression.wrap(params.default).ast
          : null,
    };

    const checked = TC.checkExpr(ast);

    if (checked instanceof TyStackTrace) {
      throw checked.toError({ jsStackPointer: this.create });
    }

    return new this(ast, checked);
  }

  isVariableIn(target: Expression | Relation | Markup): boolean {
    return (
      target.freeVars[this.exprVar.scope]?.exprs[this.exprVar.name] !==
      undefined
    );
  }

  #checkMatch(
    cases: Record<string, unknown>,
    opts: { jsStackPointer: Function }
  ): Error | null {
    if (!(this.ty.ty.k === 'enum')) {
      return new CantDoAMatchOnNonEnums({ type: this.ty })
        .toStackTrace({})
        .toError(opts);
    }

    const missingVariants = this.ty.ty.t.filter(
      (variant) => cases[variant] === undefined
    );

    if (missingVariants.length > 0) {
      return new MissingMatchVariants({ missingVariants })
        .toStackTrace({})
        .toError(opts);
    }

    return null;
  }
}
